interface ProjectCustomizationRegistry {
  schema_version: 1;
  common: string;
  users: Readonly<Record<string, string>>;
}

export interface ProjectCustomization {
  schema_version: 1;
  source: string;
  modules: Readonly<Record<string, unknown>>;
}

type RuntimeModuleImporter = (module_url: string) => Promise<unknown>;

const registry_url = new URL(
  `${import.meta.env.BASE_URL}customizations/registry.js`,
  window.location.origin,
).href;

function is_record(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function get_default_export(value: unknown): unknown {
  if (!is_record(value) || !("default" in value)) {
    throw new Error("项目定制脚本必须提供 default export");
  }
  return value.default;
}

function parse_registry(value: unknown): ProjectCustomizationRegistry {
  if (
    !is_record(value) ||
    value.schema_version !== 1 ||
    typeof value.common !== "string" ||
    !is_record(value.users)
  ) {
    throw new Error("项目定制脚本登记格式无效");
  }

  const users: Record<string, string> = {};
  for (const [user_mark, module_path] of Object.entries(value.users)) {
    if (typeof module_path !== "string") {
      throw new Error(`用户标记 ${user_mark} 的定制脚本路径无效`);
    }
    users[user_mark] = module_path;
  }

  return {
    schema_version: 1,
    common: value.common,
    users,
  };
}

function parse_customization(value: unknown): ProjectCustomization {
  if (
    !is_record(value) ||
    value.schema_version !== 1 ||
    typeof value.source !== "string" ||
    !is_record(value.modules)
  ) {
    throw new Error("项目定制脚本格式无效");
  }

  return {
    schema_version: 1,
    source: value.source,
    modules: value.modules,
  };
}

async function import_runtime_module(module_url: string): Promise<unknown> {
  return import(/* @vite-ignore */ module_url);
}

export function select_customization_path(
  registry: ProjectCustomizationRegistry,
  user_mark: string | null | undefined,
): string {
  const normalized_user_mark = user_mark?.trim() ?? "";
  return registry.users[normalized_user_mark] ?? registry.common;
}

export async function load_project_customization(
  user_mark: string | null | undefined,
  importer: RuntimeModuleImporter = import_runtime_module,
): Promise<ProjectCustomization> {
  const registry_module = await importer(registry_url);
  const registry = parse_registry(get_default_export(registry_module));
  const common_url = new URL(registry.common, registry_url).href;
  const common_module = await importer(common_url);
  const common_customization = parse_customization(
    get_default_export(common_module),
  );
  const selected_path = select_customization_path(registry, user_mark);

  if (selected_path === registry.common) {
    return common_customization;
  }

  const selected_url = new URL(selected_path, registry_url).href;
  const selected_module = await importer(selected_url);
  const selected_customization = parse_customization(
    get_default_export(selected_module),
  );
  return {
    ...selected_customization,
    modules: {
      ...common_customization.modules,
      ...selected_customization.modules,
    },
  };
}
